import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Brand assets
const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function MiningTable16() {
  const pdfRef = useRef();

  // Original Data
  const data = [
    {
      education_label: "Bachelor's degree or advanced degree",
      Coal: 0,
      Metal: 554,
      Nonmetal: 272,
      OilGas: 160,
      Support: 747,
    },
    {
      education_label:
        "Educational attainment not available (workers aged 24 or younger)",
      Coal: 0,
      Metal: 141,
      Nonmetal: 247,
      OilGas: 64,
      Support: 524,
    },
    {
      education_label: "High school or equivalent, no college",
      Coal: 0,
      Metal: 703,
      Nonmetal: 558,
      OilGas: 337,
      Support: 1237,
    },
    {
      education_label: "Less than high school",
      Coal: 0,
      Metal: 242,
      Nonmetal: 235,
      OilGas: 125,
      Support: 540,
    },
    {
      education_label: "Some college or Associate degree",
      Coal: 0,
      Metal: 819,
      Nonmetal: 530,
      OilGas: 333,
      Support: 1214,
    },
  ];

  // Chart Data
  const chartData = {
    labels: data.map((d) => d.education_label),
    datasets: [
      {
        label: "Metal Ore Mining",
        data: data.map((d) => d.Metal),
        backgroundColor: "rgba(59,130,246,0.85)", // blue
        borderRadius: 6,
      },
      {
        label: "Nonmetallic Mineral Mining and Quarrying",
        data: data.map((d) => d.Nonmetal),
        backgroundColor: "rgba(234,179,8,0.85)", // yellow
        borderRadius: 6,
      },
      {
        label: "Oil and Gas Extraction",
        data: data.map((d) => d.OilGas),
        backgroundColor: "rgba(16,185,129,0.85)", // green
        borderRadius: 6,
      },
      {
        label: "Support Activities for Mining",
        data: data.map((d) => d.Support),
        backgroundColor: "rgba(236,72,153,0.85)", // pink
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    indexAxis: "y",
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Mining Employment by Education Level (2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Number of Employees" },
        beginAtZero: true,
      },
      y: {
        ticks: { font: { size: 11 }, autoSkip: false },
      },
    },
  };

  // Summary Insights
  const totalByIndustry = {
    Metal: data.reduce((s, d) => s + d.Metal, 0),
    Nonmetal: data.reduce((s, d) => s + d.Nonmetal, 0),
    OilGas: data.reduce((s, d) => s + d.OilGas, 0),
    Support: data.reduce((s, d) => s + d.Support, 0),
  };

  const topIndustry = Object.entries(totalByIndustry).reduce((a, b) =>
    a[1] > b[1] ? a : b
  );

  const topEducation = data.reduce((a, b) => {
    const totalA = a.Metal + a.Nonmetal + a.OilGas + a.Support;
    const totalB = b.Metal + b.Nonmetal + b.OilGas + b.Support;
    return totalB > totalA ? b : a;
  });

  // PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2, // higher for sharper text
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      // A4 size in mm
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Convert canvas height to mm
      const imgWidth = pageWidth - 10; // leave small margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      // Header
      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      // Footer
      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // More pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Footer for the last page
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("utah_mining_education_levels.pdf");
      // input.style.width = originalWidth;
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-yellow-50 via-white to-blue-50 rounded-xl shadow-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Mining Employment by Education Level (2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Top Industry by Employment
            </p>
            <p className="text-lg font-bold text-blue-800">
              {topIndustry[0]} ({topIndustry[1].toLocaleString("en-US")}{" "}
              workers)
            </p>
          </div>
          <div className="p-4 bg-pink-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Top Education Level
            </p>
            <p className="text-lg font-bold text-pink-800">
              {topEducation.education_label}
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Mining Workforce (4 sectors)
            </p>
            <p className="text-lg font-bold text-green-800">
              {(
                totalByIndustry.Metal +
                totalByIndustry.Nonmetal +
                totalByIndustry.OilGas +
                totalByIndustry.Support
              ).toLocaleString("en-US")}
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col h-full">
            <div className="overflow-x-auto flex items-center">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2">Education Level</th>
                    <th className="p-2">Coal Mining</th>
                    <th className="p-2">Metal Ore</th>
                    <th className="p-2">Nonmetal</th>
                    <th className="p-2">Oil & Gas</th>
                    <th className="p-2">Support</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr
                      key={i}
                      className={`${
                        row.education_label === topEducation.education_label
                          ? "bg-pink-100 font-semibold"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <td className="p-2">{row.education_label}</td>
                      <td className="p-2">{row.Coal.toLocaleString()}</td>
                      <td className="p-2 text-blue-700">
                        {row.Metal.toLocaleString()}
                      </td>
                      <td className="p-2 text-yellow-700">
                        {row.Nonmetal.toLocaleString()}
                      </td>
                      <td className="p-2 text-green-700">
                        {row.OilGas.toLocaleString()}
                      </td>
                      <td className="p-2 text-pink-700">
                        {row.Support.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-center text-gray-700 mt-4">
          🎓 Utah’s mining workforce shows strong representation of high school
          and associate degree holders, especially in Support Activities for
          Mining and Metal Ore Mining. Bachelor's and advanced degrees
          contribute mainly within supervisory and technical roles.
        </p>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
